import { useState, useEffect } from 'react';
import { getStoreAnalytics } from '../services/api';
import { format, subDays, subMonths, subYears, startOfMonth } from 'date-fns';

const PRESETS = [
  { label: 'Last 30 days',  getDates: () => ({ from: subDays(new Date(), 30),   to: new Date() }) },
  { label: 'Last 3 months', getDates: () => ({ from: subMonths(new Date(), 3),  to: new Date() }) },
  { label: 'Last 6 months', getDates: () => ({ from: subMonths(new Date(), 6),  to: new Date() }) },
  { label: 'Last 12 months',getDates: () => ({ from: subYears(new Date(), 1),   to: new Date() }) },
  { label: 'This month',    getDates: () => ({ from: startOfMonth(new Date()),  to: new Date() }) },
];

function fmt(d) {
  return format(new Date(d), 'yyyy-MM-dd');
}

function PriceChangeBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="muted">—</span>;
  const n = Number(pct);
  const color = n > 0 ? 'var(--color-danger)' : n < 0 ? 'var(--color-success)' : 'var(--color-text-muted)';
  const prefix = n > 0 ? '+' : '';
  return (
    <span style={{
      color,
      fontWeight: 600,
      background: n > 0 ? 'var(--color-danger-bg)' : n < 0 ? 'var(--color-success-bg)' : 'transparent',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
    }}>
      {prefix}{n.toFixed(1)}%
    </span>
  );
}

export default function StoresPage() {
  const [preset, setPreset] = useState(0);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getDateRange = () => {
    if (useCustom && customFrom && customTo) {
      return { from: customFrom, to: customTo };
    }
    const { from, to } = PRESETS[preset].getDates();
    return { from: fmt(from), to: fmt(to) };
  };

  useEffect(() => {
    const { from, to } = getDateRange();
    setLoading(true);
    setError('');
    getStoreAnalytics(from, to)
      .then(setData)
      .catch(() => setError('Failed to load store data'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, useCustom, customFrom, customTo]);

  const totalSpent = data?.stores.reduce((sum, s) => sum + Number(s.total_spent), 0) ?? 0;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Stores</h2>
        <p className="muted" style={{ margin: 0 }}>
          Spending and price changes per store for the selected period
        </p>
      </div>

      {/* Period selector */}
      <div className="card" style={{ marginBottom: '1.25rem', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {PRESETS.map((p, i) => (
          <button
            key={p.label}
            className={!useCustom && preset === i ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.4rem 0.9rem', fontSize: 13 }}
            onClick={() => { setPreset(i); setUseCustom(false); }}
          >
            {p.label}
          </button>
        ))}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => { setCustomFrom(e.target.value); setUseCustom(true); }}
            style={{ width: 140, maxWidth: '100%' }}
          />
          <span className="muted">to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => { setCustomTo(e.target.value); setUseCustom(true); }}
            style={{ width: 140, maxWidth: '100%' }}
          />
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div className="stat-row">
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Total spent</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{totalSpent.toFixed(2)} kr</div>
              <div className="muted" style={{ fontSize: 12 }}>{data.from} → {data.to}</div>
            </div>
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Stores visited</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{data.stores.length}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                {[...new Set(data.stores.map((s) => s.store_chain))].join(', ')}
              </div>
            </div>
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Most visited</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {data.stores.sort((a, b) => b.receipt_count - a.receipt_count)[0]?.store_name || '—'}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {data.stores[0]?.receipt_count} receipts
              </div>
            </div>
          </div>

          {data.stores.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
              No receipts found for this period.
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Store</th>
                    <th>Chain</th>
                    <th style={{ textAlign: 'right' }}>Receipts</th>
                    <th style={{ textAlign: 'right' }}>Total spent</th>
                    <th style={{ textAlign: 'right' }}>Share</th>
                    <th style={{ textAlign: 'right' }}>Avg price/unit</th>
                    <th style={{ textAlign: 'right' }}>Price change</th>
                    <th>Last visit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stores.map((s) => (
                    <tr key={s.store_id}>
                      <td style={{ fontWeight: 500 }}>{s.store_name}</td>
                      <td className="muted">{s.store_chain}</td>
                      <td style={{ textAlign: 'right' }}>{s.receipt_count}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {Number(s.total_spent).toFixed(2)} kr
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>
                        {totalSpent > 0 ? ((Number(s.total_spent) / totalSpent) * 100).toFixed(1) : 0}%
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {s.current_avg_price ? `${Number(s.current_avg_price).toFixed(2)} kr` : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <PriceChangeBadge pct={s.price_change_pct} />
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {s.last_visit ? format(new Date(s.last_visit), 'dd MMM yyyy') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {data.stores.some((s) => s.price_change_pct !== null) && (
            <p className="muted" style={{ fontSize: 12, marginTop: '0.75rem' }}>
              Price change compares avg price/unit in the selected period vs the equal-length period before it.
            </p>
          )}
        </>
      )}
    </div>
  );
}
